import { Button, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table } from 'components/organisms';
import { useFormikContext } from 'formik';
import { useHasPermission } from 'hooks';
import { IFormikContextType } from 'hooks/useFormikField';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiNavigationArrow, PiPlus, PiTrash } from 'react-icons/pi';
import {
  IArtistExternalIdMutation,
  IArtistMutationForm,
} from 'services/artists/types';
import { useGetSalesChannelsLightList } from 'services/salesChannel';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

export const externalInitialValues: IArtistExternalIdMutation = {
  id: '',
  type: '',
  link: '',
};
//32768 - can manage
const ArtistExternal = () => {
  const {
    values,
    status,
    setFieldValue,
  }: IFormikContextType<IArtistMutationForm> =
    useFormikContext<IArtistMutationForm>();

  const hasPermission = useHasPermission();

  const { t } = useTranslation();

  const salesChannels = useGetSalesChannelsLightList();

  const salesChannelOptions =
    salesChannels.data
      ?.map(s => ({ label: s.name, value: s.name }))
      .filter(
        option =>
          !values.externalProfiles.find(
            external => external.type === option.label
          )
      ) ?? [];

  const isLatestTypeSelected = !!values.externalProfiles.length
    ? !!values.externalProfiles[values.externalProfiles.length - 1]?.type.length
    : true;

  const isDisplayMode = useMemo(
    () =>
      status?.displayMode || !hasPermission('ArtistPermissionsLIST**', 32768),
    [status?.displayMode, hasPermission('ArtistPermissionsLIST**', 32768)]
  );

  return (
    <Table
      id={'ArtistExternal'}
      size='compact'
      data={{
        nodes: values.externalProfiles.map((external, index) => ({
          externalId: external.id,
          type: external.type,
          id: index,
          link: external.link,
        })),
      }}
      columns={[
        {
          label: t('input.label.externalIdType'),
          renderCell: ({ type, id }) => (
            <TableItem justifyPosition='start'>
              {isDisplayMode ? (
                <TableItem.Text text={type} />
              ) : (
                <FormInput.Select
                  name={`externalProfiles[${id}].type`}
                  label={t(`input.label.externalIdType`)}
                  className='max-w-96'
                  options={salesChannelOptions}
                  isCreatable
                />
              )}
            </TableItem>
          ),
          resize: true,
        },
        {
          label: 'ID',
          renderCell: ({ externalId, id }) => (
            <TableItem justifyPosition='start'>
              {isDisplayMode ? (
                <TableItem.Text text={externalId} />
              ) : (
                <FormInput.Text
                  name={`externalProfiles[${id}].id`}
                  label={'ID'}
                  className='max-w-96'
                />
              )}
            </TableItem>
          ),
        },
        {
          label: '',
          renderCell: ({ id, link }) => (
            <TableItem justifyPosition='end'>
              {isDisplayMode ? (
                !!link?.length && (
                  <Button
                    isClear
                    onlyIcon
                    onClick={() => window.open(link, '_blank')}>
                                        <Button.Description description={t('button.open')} />

                                        <Button.Icon
                      icon={<PiNavigationArrow transform='scale(-1,1)' />}
                    />
                  </Button>
                )
              ) : (
                <Button
                  isClear
                  onlyIcon
                  variant={BUTTON_VARIANT.remove}
                  onClick={() =>
                    setFieldValue(
                      'externalProfiles',
                      values.externalProfiles.filter(
                        (external, externalIndex) => id !== externalIndex
                      )
                    )
                  }>
                                    <Button.Description description={t('button.remove')} />

                                    <Button.Icon icon={<PiTrash />} />
                </Button>
              )}
            </TableItem>
          ),
        },
      ]}
      tableOptions={
        isDisplayMode
          ? undefined
          : {
              renderAfterTable: () => (
                <tfoot className='full-width-footer'>
                  <TableMessage
                    className='cursor-pointer'
                    disable={!isLatestTypeSelected}
                    onClick={() =>
                      setFieldValue(
                        `externalProfiles[${values.externalProfiles.length}]`,
                        externalInitialValues
                      )
                    }>
                    <TableItem.Text text={t('button.add')} />
                    <PiPlus />
                  </TableMessage>
                </tfoot>
              ),
            }
      }
    />
  );
};

export default ArtistExternal;
